import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ScanIcon } from '../../components/ui/icons';
import * as scanner from '../../platform/scanner';
import { useScanTarget } from '../../platform/scanning';
import { useNavigation } from '../layout/navigation';
import { ScanSheet } from '../scan/ScanSheet';
import { searchSerials } from '../serials/api';

/**
 * Which part is this thing in my hand?
 *
 * The number engraved on a unit is often all there is: it came back from a
 * customer, or was found in a drawer with the label gone. Everywhere else in the
 * application a serial number is looked up *within* a part, which is no use when
 * the part is exactly what is being asked for.
 *
 * Both numbers are searched — the manufacturer's, printed on the unit, and ours,
 * on the sticker we put there — because whoever is holding it cannot be expected
 * to know whose number they are reading.
 */
export function SerialLookup() {
  const { t } = useTranslation();
  const { open } = useNavigation();

  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  const [camera, setCamera] = useState(false);
  const field = useRef<HTMLInputElement>(null);

  const submit = (value: string) => {
    const cleaned = value.trim();
    if (!cleaned) return;
    setTyped(cleaned);
    setQuery(cleaned);
  };

  // While this screen is open a scan is a lookup rather than a jump: someone
  // reading numbers off a shelf of returned units wants the answers here, not
  // to be thrown onto a different screen by the first one that resolves.
  useScanTarget(submit);

  const hits = useQuery({
    queryKey: ['serials', 'search', query],
    queryFn: () => searchSerials(query),
    enabled: query.length > 0,
  });

  const cameraReady = scanner.cameraSupport() === 'ok';

  return (
    <section className="serial-lookup">
      <form
        className="serial-lookup__form"
        onSubmit={(event) => {
          event.preventDefault();
          submit(typed);
        }}
      >
        <input
          ref={field}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          placeholder={t('stock.serials.placeholder')}
          aria-label={t('stock.serials.placeholder')}
          autoCapitalize="characters"
          spellCheck={false}
          autoFocus
        />
        <button type="submit" className="btn">
          {t('common.search')}
        </button>
        {cameraReady && (
          <button
            type="button"
            className="btn"
            onClick={() => setCamera(true)}
            aria-label={t('stock.serials.scan')}
            title={t('stock.serials.scan')}
          >
            <ScanIcon />
          </button>
        )}
      </form>

      <p className="hint">{t('stock.serials.hint')}</p>

      {camera && (
        <ScanSheet
          title={t('stock.serials.scan')}
          onRead={(code) => {
            setCamera(false);
            submit(code);
          }}
          onCancel={() => setCamera(false)}
        />
      )}

      {hits.isLoading && <p className="panel__hint">{t('common.loading')}</p>}
      {query && hits.data?.length === 0 && (
        <p className="panel__hint">{t('stock.serials.nothing', { q: query })}</p>
      )}

      <ul className="serial-lookup__hits">
        {(hits.data ?? []).map(({ unit, component }) => (
          <li key={unit.id}>
            <button type="button" className="serial-lookup__hit" onClick={() => open('component', component.id)}>
              <span className="serial-lookup__number">
                {unit.serial ?? unit.code}
                {unit.serial && <em className="components__code"> {unit.code}</em>}
              </span>
              <span className="components__name">{component.name}</span>
              <span className="components__meta">
                <em className="components__code">{component.internal_code}</em>
                <span className={`badge badge--${unit.status}`}>
                  {t(`serials.status.${unit.status}`)}
                </span>
                {unit.location_name && <em>{unit.location_name}</em>}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
